import { enableGridlines, isCustomLabelProcessingDisabled, isLayoutOptimizationsEnabled, } from '@thoughtspot/session-service';
import _ from 'lodash';
import React from 'react';
import { ChartEventTypes, } from '..';
import { ChartLegend } from '../components/chart-legend';
import { isLeftPositionedLegend, isTopPositionedLegend, } from '../components/chart-legend-utils';
import { isSnapshotResizeEnabled } from '../components/chart-viz/canvas-snapshot';
import { getChartType, isHighchartsProvider, isResponsiveLayoutEnabled, } from '../core/chart-model-utils';
import { chartProviders, configOptions, getChartProvider, hasAxisLabels, hasNativeLegend, } from '../core/chart-type-specification-service';
import { canShowHighchartTitle, canShowLegend, getGridLinesConfig, getLegendOrientation, LegendOrientation, showLegend, } from '../core/chart-ui-utils';
import { getResponsiveBreakpoint, SCALING_CLASS_NAMES, } from '../core/scaling-service';
import { getSeries } from '../data/data-util';
import { sweepColumnControlHosts } from '../devextreme/components/column-control-roots';
import { getEnvFlags } from '../env';
import { getHighchartScreenConfig } from '../highcharts/chart-theme-screen-service';
import { chartUIRenderHighchart, updateChartPropertiesOnResize, } from '../highcharts/highchart-chart-util';
import { getConfigForRotatedLabels, getEffectiveFontSizeForLegend, } from '../highcharts/highchart-theme-screen-util';
import { postProcessHighchartDataLabels, postProcessHighchartXAxisLabels, } from '../highcharts/highcharts-label-processor-service';
import { chartUIRenderInsightHighchart } from '../insights/insight-chart-utils';
import { chartUIRenderReactComp } from '../reactcharts';
import { ChartType } from '../types';
import { createLogger } from '../utils/logger';
import { clearMarks, clearMeasures, getEntriesByName, logMeasures, mark, measure, } from '../utils/perf-utils';
import { renderIntoContainer, unmountContainer, } from '../utils/react-root.util';
const logger = createLogger('chart-lib-rendere-index');
export const CHART_CONTAINER_CLASSNAME = 'chart-area';
export const LEGEND_CONTAINER_CLASSNAME = 'legend-area';
export const CHART_SECTION = 'chartSection';
export const CHART_POINT_INTERACT = 'chartPointInteract';
export const VIZ_WRAPPER = 'vizWrapper';
export const HORIZONTAL_LEGEND = 'horizontal-legend';
export const VERTICAL_LEGEND_CONTAINER = 'vertical-legend-container';
export const LEGEND_WIDTH = 'legend-width';
export const LEFT_LEGEND = 'left-legend';
export const TOP_LEGEND = 'top-legend';
export const ZOOM_MODE_CLASSNAME = 'zoom-mode';
const chartSuffix = '-chart';
export const getStyleClass = () => {
    return 'vizChart';
};
export const unmountChartReactRoots = (vizContainerElement) => {
    if (!vizContainerElement) {
        return;
    }
    try {
        sweepColumnControlHosts(vizContainerElement);
        vizContainerElement
            .querySelectorAll(`.${CHART_CONTAINER_CLASSNAME}, .${LEGEND_CONTAINER_CLASSNAME}`)
            .forEach(container => {
            unmountContainer(container);
        });
        unmountContainer(vizContainerElement);
    }
    catch (error) {
        logger.warn('Error unmounting chart react roots', error);
    }
};
function createContainer(className = '') {
    const el = document.createElement('div');
    el.setAttribute('class', className);
    return el;
}
const clearClassNameForScreen = (vizContainerElement) => {
    const chartScalingClasses = SCALING_CLASS_NAMES.map(c => c + chartSuffix);
    vizContainerElement.classList.remove(...chartScalingClasses);
};
const updateClassNameForScreen = (vizContainerElement) => {
    clearClassNameForScreen(vizContainerElement);
    const currentBreakpoint = getResponsiveBreakpoint();
    vizContainerElement.classList.add(currentBreakpoint.className + chartSuffix);
};
export const updateClassNameForZoom = (vizContainerElement) => {
    const vizWrapper = vizContainerElement.getElementsByClassName(VIZ_WRAPPER)[0];
    vizWrapper.classList.toggle(ZOOM_MODE_CLASSNAME);
};
async function getChartRendererForProvider(chartProvider) {
    switch (chartProvider) {
        case chartProviders.HIGHCHART:
            return chartUIRenderHighchart;
        case chartProviders.GEO_MAP:
            return (await import('../geomap')).chartUIRenderGeoMap;
        case chartProviders.GRID_TABLE:
        case chartProviders.PIVOT_TABLE:
            return (await import('../devextreme')).chartUIRenderDXPivot;
        case chartProviders.NETWORK:
            return (await import('../network-map')).chartUIRenderNetworkMap;
        case chartProviders.REACT_COMPONENT:
            return chartUIRenderReactComp;
        case chartProviders.INSIGHT:
            return chartUIRenderInsightHighchart;
        default:
            return null;
    }
}
const renderLegend = (chartObj, legendEl, chartModel, dataModel, eventsMap, getTranslatedText, vizContainerWidth, fontSize, isLegendVertical, allowColorChange, isPresented) => {
    mark('startRenderLegend');
    if (!showLegend(chartModel, dataModel)) {
        return;
    }
    const onLegendClick = eventsMap.get(ChartEventTypes.LegendClick);
    return new Promise((resolve, reject) => {
        try {
            renderIntoContainer(React.createElement(ChartLegend, { chartObj: chartObj, chartModel: chartModel, dataModel: dataModel, isLegendVertical: isLegendVertical, onLegendClick: onLegendClick, onUpdateColor: eventsMap.get(ChartEventTypes.LegendColorChange), getTranslatedText: getTranslatedText, vizContainerWidth: vizContainerWidth, fontSize: fontSize, allowColorChange: allowColorChange, isPresented: isPresented }), legendEl, () => {
                mark('endRenderLegend');
                measure('renderLegend', 'startRenderLegend', 'endRenderLegend');
                logMeasures(getEntriesByName('renderLegend', 'measure'), 'renderLegend');
                clearMarks(['startRenderLegend', 'endRenderLegend']);
                clearMeasures(['renderLegend']);
                resolve();
            });
        }
        catch (error) {
            reject(error);
        }
    });
};
const isLegendHorizontal = (chartModel, chartDimensions, dataModel) => {
    return (getLegendOrientation(chartModel, chartDimensions, dataModel) === LegendOrientation.Horizontal);
};
export const isLegendVertical = (chartModel, chartDimensions, dataModel) => {
    return (chartModel.config.chartType === ChartType.Sankey ||
        (!hasNativeLegend(chartModel) &&
            getLegendOrientation(chartModel, chartDimensions, dataModel) === LegendOrientation.Vertical));
};
export const _canShowLegend = (chartModel, dataModel, chartDimensions) => {
    return canShowLegend(chartModel, dataModel, chartDimensions, isLegendVertical(chartModel, chartDimensions, dataModel));
};
export const render = async (chartObj, vizContainerElement, chartType, chartModel, dataModel, eventsMap, renderOptions, env, prevRenderState, chartOptions, error) => {
    var _a, _b;
    const frag = new DocumentFragment();
    const chartSection = createContainer(CHART_SECTION);
    const chartWrapper = createContainer(VIZ_WRAPPER);
    const chartContainer = createContainer(CHART_CONTAINER_CLASSNAME);
    const legendContainer = createContainer(LEGEND_CONTAINER_CLASSNAME);
    chartSection.append(chartWrapper);
    chartWrapper.append(chartContainer);
    chartWrapper.append(legendContainer);
    if (getEnvFlags().isFlipTooltipToContextMenuEnabled) {
        chartSection.classList.add(CHART_POINT_INTERACT);
    }
    frag.appendChild(chartSection);
    const preRenderDims = isLayoutOptimizationsEnabled()
        ? vizContainerElement.getBoundingClientRect()
        : null;
    unmountChartReactRoots(vizContainerElement);
    vizContainerElement.innerHTML = '';
    vizContainerElement.appendChild(frag);
    if (chartType === ChartType.Sankey) {
        vizContainerElement.style.marginTop = '14px';
    }
    else {
        vizContainerElement.style.marginTop = '';
    }
    const { width: vizContainerWidth, height: vizContainerHeight } = preRenderDims !== null && preRenderDims !== void 0 ? preRenderDims : vizContainerElement.getBoundingClientRect();
    if (isLegendHorizontal(chartModel, {
        width: vizContainerWidth,
        height: vizContainerHeight,
    }, dataModel)) {
        chartWrapper.classList.add(HORIZONTAL_LEGEND);
        legendContainer.classList.remove(LEGEND_WIDTH);
        legendContainer.classList.remove(VERTICAL_LEGEND_CONTAINER);
    }
    else {
        chartWrapper.classList.remove(HORIZONTAL_LEGEND);
        legendContainer.classList.add(LEGEND_WIDTH);
        legendContainer.classList.add(VERTICAL_LEGEND_CONTAINER);
    }
    renderOptions.shouldShowHighchartTitle = canShowHighchartTitle(chartOptions.shouldShowHighchartTitle, vizContainerWidth, chartModel);
    renderOptions.shouldShowExplainChange =
        chartOptions.shouldShowExplainChange;
    const chartProvider = getChartProvider(chartType, !!chartModel.cortexData);
    const renderChart = await getChartRendererForProvider(chartProvider);
    const chart = await renderChart(chartObj, chartContainer, chartModel, dataModel, eventsMap, renderOptions, env, chartOptions.isPrintMode);
    if (chart &&
        _canShowLegend(chartModel, dataModel, {
            width: vizContainerWidth,
            height: vizContainerHeight,
        })) {
        const enableGridline = enableGridlines();
        const { xGridlineEnabled, yGridlineEnabled } = getGridLinesConfig(chartModel, enableGridline);
        const defaultScreenConfig = { xGridlineEnabled, yGridlineEnabled };
        const axisDetails = {
            x: (_a = chart.xAxis) !== null && _a !== void 0 ? _a : {},
            y: (_b = chart.yAxis) !== null && _b !== void 0 ? _b : {},
        };
        const chartConfig = getHighchartScreenConfig(vizContainerWidth, defaultScreenConfig, axisDetails, chartOptions.shouldScaleFonts, chartModel, dataModel, renderOptions === null || renderOptions === void 0 ? void 0 : renderOptions.optSupportAnimation);
        await renderLegend(chartObj, legendContainer, chartModel, dataModel, eventsMap, env.getTranslatedText, vizContainerWidth, chartConfig === null || chartConfig === void 0 ? void 0 : chartConfig.config.legend.itemStyle.fontSize, isLegendVertical(chartModel, {
            width: vizContainerWidth,
            height: vizContainerHeight,
        }, dataModel), !env.flags.allowMobileOverride, chartOptions.shouldScaleFonts);
    }
    prevRenderState = {
        ...prevRenderState,
        lastScreenSize: getResponsiveBreakpoint().screenSizeClass,
        vizContainer: {
            prevVizContainerWidth: vizContainerWidth,
            prevVizContainerHeight: vizContainerHeight,
        },
    };
    return {
        chart,
        prevRenderState,
    };
};
export async function reflow(chartObj, chart, vizModel, dataModel, vizContainerElement, chartOptions, prevRenderState, eventsMap, envOptions, renderOptions) {
    var _a, _b, _c, _d, _e, _f, _g;
    if (!vizModel || !chart) {
        return {
            prevRenderState,
            skipReason: 'no_chart',
            setsizeCalled: false,
            rafUsed: false,
            breakpointChanged: false,
            legendUnmounted: false,
            legendRemounted: false,
        };
    }
    let legendUnmounted = false;
    let legendRemounted = false;
    let setsizeCalled = false;
    let rafUsed = false;
    const updateAnimation = !chartOptions.isLiveboardContext || !isSnapshotResizeEnabled();
    const newRenderState = _.assign({}, prevRenderState);
    const initialContainerRect = vizContainerElement.getBoundingClientRect();
    const { width: newVizContainerWidth, height: newVizContainerHeight, } = initialContainerRect;
    let chartConfig;
    const hasScalingChanged = chartOptions.shouldScaleFonts !== prevRenderState.lastRenderScaled;
    const currentScreenSize = getResponsiveBreakpoint().screenSizeClass;
    const hasBreakPointChanged = !_.isEqual(currentScreenSize, prevRenderState.lastScreenSize);
    newRenderState.lastRenderScaled = chartOptions.shouldScaleFonts;
    newRenderState.lastScreenSize = currentScreenSize;
    if (chartOptions.shouldScaleFonts) {
        updateClassNameForScreen(vizContainerElement);
    }
    else {
        clearClassNameForScreen(vizContainerElement);
    }
    if (_.isBoolean(renderOptions === null || renderOptions === void 0 ? void 0 : renderOptions.xGridlineEnabled) ||
        _.isBoolean(renderOptions === null || renderOptions === void 0 ? void 0 : renderOptions.yGridlineEnabled)) {
        (_b = (_a = chart).enableXAxisGridLines) === null || _b === void 0 ? void 0 : _b.call(_a, renderOptions.xGridlineEnabled);
        (_d = (_c = chart).enableYAxisGridLines) === null || _d === void 0 ? void 0 : _d.call(_c, renderOptions.yGridlineEnabled);
    }
    chart.setScaledFonts(chartOptions.shouldScaleFonts);
    const shouldResize = isHighchartsProvider(vizModel) &&
        (hasScalingChanged || hasBreakPointChanged);
    let isLegendEnabled = true;
    if (shouldResize) {
        const axisDetails = {
            x: chart.xAxis[0],
            y: chart.yAxis,
        };
        const enableGridline = enableGridlines();
        const { xGridlineEnabled, yGridlineEnabled } = getGridLinesConfig(vizModel, enableGridline);
        const defaultScreenConfig = { xGridlineEnabled, yGridlineEnabled };
        chartConfig = getHighchartScreenConfig(newVizContainerWidth, defaultScreenConfig, axisDetails, chartOptions.shouldScaleFonts, vizModel, dataModel, renderOptions === null || renderOptions === void 0 ? void 0 : renderOptions.optSupportAnimation);
        chart.update(chartConfig.config, true, false, updateAnimation);
        isLegendEnabled = _.get(chartConfig, 'meta.legendEnabled');
    }
    const bypassLabelsProcessing = !isHighchartsProvider(vizModel);
    let dontResize = false;
    const vizContainerDimensions = isLayoutOptimizationsEnabled()
        ? initialContainerRect
        : vizContainerElement.getBoundingClientRect();
    const chartContainer = vizContainerElement.getElementsByClassName(CHART_CONTAINER_CLASSNAME)[0];
    const legendContainer = vizContainerElement.getElementsByClassName(LEGEND_CONTAINER_CLASSNAME)[0];
    const { width: vizContainerWidth } = vizContainerDimensions;
    const fontSize = getEffectiveFontSizeForLegend(vizModel, chartConfig, chartOptions.shouldScaleFonts);
    if (_canShowLegend(vizModel, dataModel, vizContainerDimensions)) {
        if (isLegendHorizontal(vizModel, vizContainerDimensions, dataModel)) {
            const vizWrapper = vizContainerElement.getElementsByClassName(VIZ_WRAPPER)[0];
            vizWrapper.classList.add(HORIZONTAL_LEGEND);
            legendContainer.classList.remove(LEGEND_WIDTH);
            if (isTopPositionedLegend(vizModel))
                vizWrapper.classList.add(TOP_LEGEND);
        }
        else {
            const vizWrapper = vizContainerElement.getElementsByClassName(VIZ_WRAPPER)[0];
            vizWrapper.classList.remove(HORIZONTAL_LEGEND);
            legendContainer.classList.add(LEGEND_WIDTH);
            if (isLeftPositionedLegend(vizModel))
                vizWrapper.classList.add(LEFT_LEGEND);
        }
        await renderLegend(chartObj, legendContainer, vizModel, dataModel, eventsMap, envOptions.getTranslatedText, vizContainerWidth, fontSize, isLegendVertical(vizModel, vizContainerDimensions, dataModel), !envOptions.flags.allowMobileOverride, chartOptions.shouldScaleFonts);
        legendRemounted = true;
    }
    else if (legendContainer === null || legendContainer === void 0 ? void 0 : legendContainer.hasChildNodes) {
        try {
            unmountContainer(legendContainer);
            legendUnmounted = true;
        }
        catch (error) {
            logger.warn('Error unmounting legend component', error);
        }
    }
    const prevVizContainerWidth = ((_e = prevRenderState.vizContainer) === null || _e === void 0 ? void 0 : _e.prevVizContainerWidth) || void 0;
    const prevVizContainerHeight = ((_f = prevRenderState.vizContainer) === null || _f === void 0 ? void 0 : _f.prevVizContainerHeight) || void 0;
    const { width: chartContainerWidth, height: chartContainerHeight, } = chartContainer.getBoundingClientRect();
    const { width: legendContainerWidth = 0, height: legendContainerHeight = 0, } = (_g = legendContainer === null || legendContainer === void 0 ? void 0 : legendContainer.getBoundingClientRect()) !== null && _g !== void 0 ? _g : {};
    if (prevVizContainerWidth !== void 0 && prevVizContainerHeight !== void 0) {
        if (newVizContainerWidth === prevVizContainerWidth &&
            newVizContainerHeight === prevVizContainerHeight &&
            chartContainerWidth === prevVizContainerWidth &&
            chartContainerHeight === prevVizContainerHeight) {
            dontResize = true;
        }
    }
    if (newVizContainerWidth <= 0 || newVizContainerHeight <= 0) {
        dontResize = true;
    }
    newRenderState.vizContainer = {
        prevVizContainerWidth: newVizContainerWidth,
        prevVizContainerHeight: newVizContainerHeight,
    };
    if (dontResize) {
        if (!bypassLabelsProcessing && !isCustomLabelProcessingDisabled()) {
            requestAnimationFrame(() => {
                postProcessHighchartDataLabels(chart);
                postProcessHighchartXAxisLabels(chart);
            });
        }
        const skipReason = newVizContainerWidth <= 0 || newVizContainerHeight <= 0
            ? 'invalid_dimensions'
            : 'same_size';
        return {
            prevRenderState: newRenderState,
            skipReason,
            setsizeCalled: false,
            rafUsed: false,
            breakpointChanged: hasBreakPointChanged,
            legendUnmounted,
            legendRemounted,
        };
    }
    if (isHighchartsProvider(vizModel)) {
        chart.toggleAxises();
        const shouldShowHighchartTitle = canShowHighchartTitle(chartOptions.shouldShowHighchartTitle, newVizContainerWidth, vizModel);
        chart.update({
            title: {
                style: {
                    display: shouldShowHighchartTitle ? 'block' : 'none',
                },
            },
            ...(getChartType(vizModel) !== ChartType.Treemap &&
                getConfigForRotatedLabels(chart.xAxis[0], vizModel, dataModel)),
        }, true, false, updateAnimation);
    }
    const responsiveLayoutDisabled = configOptions(getChartType(vizModel), vizModel).allowedConfigurations
        .advanced.display.responsiveLayout &&
        !isResponsiveLayoutEnabled(vizModel);
    let isExistingAxisInstanceDestroyed = false;
    if (responsiveLayoutDisabled) {
        chart.updateXaxisScrollConfig(getSeries(dataModel), getChartType(vizModel), newVizContainerWidth, newVizContainerHeight, vizModel, dataModel);
        isExistingAxisInstanceDestroyed = true;
    }
    let chartWidth = newVizContainerWidth;
    let chartHeight = newVizContainerHeight;
    if (_canShowLegend(vizModel, dataModel, vizContainerDimensions)) {
        chartWidth -= isLegendVertical(vizModel, vizContainerDimensions, dataModel)
            ? legendContainerWidth
            : 0;
        chartHeight -= isLegendHorizontal(vizModel, vizContainerDimensions, dataModel)
            ? legendContainerHeight
            : 0;
    }
    chart.setSize(chartWidth, chartHeight, false);
    setsizeCalled = true;
    if (chartOptions.shouldScaleChart &&
        isHighchartsProvider(vizModel) &&
        hasAxisLabels(getChartType(vizModel))) {
        updateChartPropertiesOnResize(chart, vizModel, dataModel);
    }
    if (!bypassLabelsProcessing && !isCustomLabelProcessingDisabled()) {
        rafUsed = true;
        requestAnimationFrame(() => {
            postProcessHighchartDataLabels(chart);
            postProcessHighchartXAxisLabels(chart);
        });
    }
    return {
        prevRenderState: newRenderState,
        skipReason: '',
        setsizeCalled,
        rafUsed,
        breakpointChanged: hasBreakPointChanged,
        legendUnmounted,
        legendRemounted,
    };
}
//# sourceMappingURL=index.js.map